// LLM generated - Model Claude 4 Sonnet
import { useMemo, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { useExpirationCountdown } from '~/domains/common/hooks/useExpirationCountdown';
import { VoucherType, PromoType } from '~/domains/promotions/types/enums';
import type { PromoCardProps } from '~/domains/promotions/types/carousel.types';

/**
 * Visibility threshold for the expiration pill.
 *
 * The pill is an *urgency cue*, not a date label — it should only render when
 * the expiration is close enough to influence user behavior. We hide it when
 * the expiration is more than 7 days away.
 *
 * The 7-day cutoff aligns with `useExpirationCountdown`, which switches its
 * formatted output from countdown-style ("3d", "1d 4h", "HH:MM:SS") to a
 * static date ("MM/dd/yy") at the same boundary — past that point the pill
 * is no longer time-sensitive information and adds noise to the card.
 *
 * Expired cards (deadline already passed) always show the pill regardless of
 * threshold, since "Expired" is itself an actionable signal.
 */
// Threshold is 7 days
const EXPIRATION_PILL_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

type UsePromoCardProps = {
  type: PromoCardProps['type'];
  boostType?: PromoCardProps['boostType'];
  voucherType?: PromoCardProps['voucherType'];
  restrictedContestId?: PromoCardProps['restrictedContestId'];
  actionText?: PromoCardProps['actionText'];
  expirationDate?: PromoCardProps['expirationDate'];
  disabled?: PromoCardProps['disabled'];
  isSelected?: PromoCardProps['isSelected'];
  isApplied?: PromoCardProps['isApplied'];
  alwaysShowExpiration?: PromoCardProps['alwaysShowExpiration'];
  onAction?: PromoCardProps['onAction'];
  onActionLeft?: PromoCardProps['onActionLeft'];
  onActionRight?: PromoCardProps['onActionRight'];
};

type UsePromoCardReturn = {
  displayActionText: string | null;
  expirationChipLabel: string | null;
  isDisabled: boolean;
  isClickable: boolean;
  isLeftClickable: boolean;
  isRightClickable: boolean;
  hasDeadlinePassed: boolean;
};

export const usePromoCard = ({
  type,
  voucherType,
  actionText,
  expirationDate,
  disabled = false,
  isSelected = false,
  isApplied = false,
  alwaysShowExpiration = false,
  onAction,
  onActionLeft,
  onActionRight,
}: UsePromoCardProps): UsePromoCardReturn => {
  const { t } = useTranslation('promotions');

  // Convert expirationDate to Date object if it's a string
  const expirationDateObj = useMemo(() => {
    if (!expirationDate) return null;
    return typeof expirationDate === 'string' ? new Date(expirationDate) : expirationDate;
  }, [expirationDate]);

  // Stable fallback Date so `useExpirationCountdown`'s effect (which depends on
  // targetDate identity) doesn't re-run every render when no expiration is set.
  // Without this the hook loops setState → render → new Date() → setState.
  const fallbackDateRef = useRef<Date>();
  if (!fallbackDateRef.current) fallbackDateRef.current = new Date();

  const countdownResult = useExpirationCountdown(expirationDateObj || fallbackDateRef.current);

  const timeLeft = expirationDateObj ? countdownResult.value : '';
  const hasDeadlinePassed = expirationDateObj ? countdownResult.hasDeadlinePassed : false;

  const displayActionText = useMemo(() => {
    if (isApplied) {
      return t('promoCard.actions.applied');
    }

    if (isSelected) {
      return t('promoCard.actions.remove');
    }

    // Override action text if provided
    if (actionText) return actionText;

    if (type === PromoType.VOUCHER && voucherType === VoucherType.CONTEST_ID_RESTRICTED) {
      return t('promoCard.actions.enter');
    }

    if (type === PromoType.DEPOSIT_BONUS) {
      return t('promoCard.actions.deposit');
    }

    return t('promoCard.actions.apply');
  }, [type, voucherType, actionText, isSelected, t]);

  const expirationChipLabel = useMemo<string | null>(() => {
    if (!expirationDateObj || !timeLeft || isApplied) return null;
    if (hasDeadlinePassed) return t('promoCard.expiration.expired');

    const remainingMs = expirationDateObj.getTime() - Date.now();
    if (!alwaysShowExpiration && remainingMs > EXPIRATION_PILL_THRESHOLD_MS) return null;

    return `${t('promoCard.expiration.expires')} ${timeLeft}`;
  }, [hasDeadlinePassed, timeLeft, expirationDateObj, isApplied, alwaysShowExpiration, t]);

  const isDisabled = disabled || hasDeadlinePassed;
  const isClickable = !isApplied && !isDisabled && (!!onAction || !!onActionLeft);
  const hasSplitActions = !onAction && (!!onActionLeft || !!onActionRight);
  const isLeftClickable = !isDisabled && hasSplitActions && !!onActionLeft;
  const isRightClickable = !isApplied && !isDisabled && hasSplitActions && !!onActionRight;

  return {
    displayActionText,
    expirationChipLabel,
    isDisabled,
    isClickable,
    isLeftClickable,
    isRightClickable,
    hasDeadlinePassed,
  };
};
